import {
  MAX_PERF_OBSERVATION_DURATION_MS,
  PERF_WINDOW_VITAL_NAME,
} from './constants';
import type {LoafObservationRuntime} from './loafObservationRuntime';
import {
  createSamplingPolicy,
  getTriggerSettings,
  type SamplingPolicy,
} from './samplingPolicy';
import type {
  PerfWindowCallerContext,
  PerfWindowOutcome,
  PerfWindowReason,
  PerfWindowSessionHandle,
  PerfWindowSessionOptions,
  PerfWindowTrigger,
  PerfWindowVitalOptions,
  RecordPerfWindowVital,
} from './types';
import {waitForPerfWindowDelivery} from './waitForPerfWindowDelivery';

interface LifecycleObservation {
  id: number;
  trigger: PerfWindowTrigger;
  workflow: string;
  workflowContext?: PerfWindowCallerContext;
  startTime: number;
  maxDurationMs: number;
  minStartIntervalMs: number;
  endTime?: number;
  backstopTimeoutId: number;
  backgroundedAt?: number;
  outcome?: PerfWindowOutcome;
  reason?: PerfWindowReason;
  cancelDelivery?: () => void;
}

export class LifecyclePerfObserver {
  private readonly observations = new Map<number, LifecycleObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordVital: RecordPerfWindowVital,
    private readonly samplingPolicy: SamplingPolicy = createSamplingPolicy()
  ) {}

  /**
   * Starts measuring until the caller calls complete(), for example when a hover ends.
   * If complete() is never called, maxDurationMs stops new frames from being included
   * and the collected measurements are reported with outcome: 'timed-out'.
   */
  begin(
    options: PerfWindowSessionOptions
  ): PerfWindowSessionHandle | undefined {
    const {trigger, workflow} = options;
    const settings = getTriggerSettings(trigger);
    const minStartIntervalMs = Math.max(
      settings.minIntervalMs,
      options.minStartIntervalMs ?? 0
    );
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible' ||
      !settings.enabled ||
      !this.isValid(options) ||
      !this.samplingPolicy.isCooldownElapsed(
        trigger,
        workflow,
        performance.now(),
        minStartIntervalMs
      )
    ) {
      return undefined;
    }
    const started = this.runtime.createCollector(options.maxDurationMs);
    if (started == null) {
      return undefined;
    }

    this.samplingPolicy.recordStart(trigger, workflow, started.startTime);
    const observation: LifecycleObservation = {
      id: started.collectorId,
      trigger,
      workflow,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      startTime: started.startTime,
      maxDurationMs: options.maxDurationMs,
      minStartIntervalMs,
      backstopTimeoutId: 0,
    };
    observation.backstopTimeoutId = window.setTimeout(
      () =>
        this.queueCompletion(
          observation,
          observation.startTime + observation.maxDurationMs,
          'timed-out'
        ),
      options.maxDurationMs
    );
    this.observations.set(observation.id, observation);

    return {
      complete: () => this.completeObservation(observation.id),
      cancel: () => this.cancelObservation(observation.id),
    };
  }

  dispose(): void {
    for (const observation of this.observations.values()) {
      this.discardObservation(observation);
    }
  }

  markBackgrounded(): void {
    for (const observation of this.observations.values()) {
      observation.backgroundedAt ??= performance.now();
    }
  }

  handlePageAbandonment(reason: 'pagehide' | 'freeze'): void {
    this.markBackgrounded();
    const abandonTime = performance.now();
    for (const observation of this.observations.values()) {
      if (observation.endTime == null) {
        const endTime = Math.min(
          abandonTime,
          observation.startTime + observation.maxDurationMs
        );
        if (!this.runtime.sealCollector(observation.id, endTime)) {
          this.discardObservation(observation);
          continue;
        }
        observation.endTime = endTime;
        observation.outcome = 'abandoned';
        observation.reason = reason;
      }
      // The page may stop running now, so report the measurements already available.
      this.finishObservation(observation.id, false);
    }
  }

  private isValid(options: PerfWindowSessionOptions) {
    return (
      options.workflow.length > 0 &&
      (options.minStartIntervalMs == null ||
        (Number.isFinite(options.minStartIntervalMs) &&
          options.minStartIntervalMs >= 0)) &&
      Number.isFinite(options.maxDurationMs) &&
      options.maxDurationMs > 0 &&
      options.maxDurationMs <= MAX_PERF_OBSERVATION_DURATION_MS
    );
  }

  private completeObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    const now = performance.now();
    const cutoff = observation.startTime + observation.maxDurationMs;
    if (now >= cutoff) {
      this.queueCompletion(observation, cutoff, 'timed-out');
      return false;
    }
    this.queueCompletion(observation, now, 'completed');
    return true;
  }

  private queueCompletion(
    observation: LifecycleObservation,
    endTime: number,
    outcome: 'completed' | 'timed-out'
  ): void {
    if (observation.endTime != null) {
      return;
    }
    if (!this.runtime.sealCollector(observation.id, endTime)) {
      this.discardObservation(observation);
      return;
    }
    observation.endTime = endTime;
    observation.outcome = outcome;
    window.clearTimeout(observation.backstopTimeoutId);
    // Stop including frames that start at or after endTime. A frame already in progress
    // may finish later; wait for its timing data before calculating the report.
    observation.cancelDelivery = waitForPerfWindowDelivery(deliveryComplete =>
      this.finishObservation(observation.id, deliveryComplete)
    );
  }

  private cancelObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    this.discardObservation(observation);
    return true;
  }

  private finishObservation(id: number, deliveryComplete: boolean): void {
    const observation = this.observations.get(id);
    if (observation?.endTime == null || observation.outcome == null) {
      return;
    }
    const requestedDuration = Math.max(
      0,
      observation.endTime - observation.startTime
    );
    const result = this.runtime.finalizeCollector(
      observation.id,
      requestedDuration
    );
    if (result == null) {
      this.discardObservation(observation);
      return;
    }

    this.clearObservationResources(observation);
    this.observations.delete(id);
    const {duration, measurementComplete, ...metrics} = result;
    const reason: PerfWindowReason | undefined =
      observation.reason ??
      (!deliveryComplete
        ? 'delivery-timeout'
        : !measurementComplete
          ? 'entries-dropped'
          : undefined);
    const vital: PerfWindowVitalOptions = {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: {
        trigger: observation.trigger,
        mode: 'session',
        workflow: observation.workflow,
        outcome: observation.outcome,
        ...(reason == null ? {} : {reason}),
        backgrounded:
          observation.backgroundedAt != null &&
          observation.backgroundedAt <= observation.startTime + duration,
        measurementComplete: deliveryComplete && measurementComplete,
        minStartIntervalMs: observation.minStartIntervalMs,
        maxDurationMs: observation.maxDurationMs,
        ...(observation.workflowContext == null
          ? {}
          : {workflowContext: observation.workflowContext}),
        ...metrics,
      },
    };
    this.recordVital(PERF_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: LifecycleObservation): void {
    if (!this.observations.has(observation.id)) {
      return;
    }
    this.clearObservationResources(observation);
    this.observations.delete(observation.id);
    this.runtime.cancelCollector(observation.id);
  }

  private clearObservationResources(observation: LifecycleObservation): void {
    window.clearTimeout(observation.backstopTimeoutId);
    observation.cancelDelivery?.();
  }
}
